const capitalizeFirstLetter = (str) => {
  if (!str) return '';
  return str
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const sortColumns = (columns, columnOrder) => {
  return columns
    .filter((obj) => Object.keys(obj).length !== 0)
    .filter((column) => columnOrder.includes(column.title))
    .sort((a, b) => columnOrder.indexOf(a.title) - columnOrder.indexOf(b.title));
};

const removeColumns = (columns, columnsToRemove) => {
  return columns
    .filter((obj) => Object.keys(obj).length !== 0)
    .filter((column) => !columnsToRemove.includes(column.title));
};

const replaceEmptyRecords = (record) => {
  Object.keys(record).forEach((key) => {
    if (record[key] === '' || record[key] === undefined) {
      record[key] = '--';
    }
  });
  return record;
};

const sortByActive = (data) => {
  return data.sort((a, b) => b.active - a.active);
};

const multiply = (a, b) => {
  const precision = 1000000;
  return (a * precision * (b * precision)) / (precision * precision);
};

const sum = (...numbers) => {
  const factor = Math.pow(10, 10);
  return numbers.reduce((sum, num) => {
    return (Math.round(sum * factor) + Math.round(num * factor)) / factor;
  }, 0);
};

const hasData = (data) => {
  if (data === null) {
    return false;
  }

  if (Array.isArray(data)) {
    return data.length > 0;
  }

  if (typeof data === 'object') {
    return Object.keys(data).some((key) => {
      const value = data[key];
      return value !== null && hasData(value);
    });
  }

  return false;
};

const hasValue = (value) => value !== '' && value !== null && value !== undefined;

const currencyFormatter = (value = 0) => `$ ${Number(value).toFixed(2)}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

const noFormatCurrency = (value = 0) => {
  if (value && typeof value === 'string') {
    return parseFloat(value.replace(/[^0-9.-]+/g, '')).toFixed(2);
  }
  return value;
};

const valueToUpperCase = (value, form, field) => {
  if (value) {
    form.setFieldsValue({ [field]: value.toUpperCase() });
  }
};
/**
 * Prevents non-numeric and non-decimal characters from being typed in the input.
 * @param {Event} e - The event that triggered the function.
 * @param {boolean} [allowDecimal=false] - Whether to allow decimal points.
 * @returns {void}
 */
const allowOnlyNumbers = (e, allowDecimal = false) => {
  const charCode = e.which ? e.which : e.keyCode;
  const inputValue = e.target.value;
  // Allow special keys: Backspace (8), Tab (9), Enter (13), Delete (46), Arrow keys (37-40)
  if (charCode === 8 || charCode === 9 || charCode === 13 || charCode === 46 || (charCode >= 37 && charCode <= 40)) {
    return;
  }
  // Allow decimal point if enabled and not already present in the input
  if (allowDecimal && (charCode === 190 || charCode === 110)) {
    if (inputValue.includes('.')) {
      e.preventDefault();
    }
    return;
  }
  // Allow numbers from both the top row (48-57) and the numeric keypad (96-105)
  if ((charCode >= 48 && charCode <= 57) || (charCode >= 96 && charCode <= 105)) {
    return;
  }
  e.preventDefault();
};

const fillCeroIfEmpty = (value, form, field) => {
  if (value === '') {
    form.setFieldsValue({ [field]: 0 });
  }
};

const isEdit = (mode) => mode === 'edit';
const isCreate = (mode) => mode === 'create';

/**
 * Builds an income URL based on the provided parameters.
 *
 * @param {Object} params - The parameters to build the URL.
 * @param {string} params.userId - The user ID.
 * @param {boolean} params.active - Indicates if it is active.
 * @param {string} params.incomeTypeOid - The income type OID.
 * @param {Object} params.fromDate - The starting date of the period.
 * @param {Object} params.toDate - The ending date of the period.
 * @returns {string} The URL built with the provided parameters.
 */
const buildIncomeUrl = ({ userId, active, incomeTypeOid, fromDate, toDate }) => {
  return `/income/user/${userId}/active/${active}/incomeType/${incomeTypeOid}/fromOperationDate/${fromDate.format('YYYY-MM-DD')}/toOperationDate/${toDate.format('YYYY-MM-DD')}`;
};

const getSelectOptions = (items, labelKey = 'description', valueKey = 'oid') =>
  items.map((item) => ({ label: item[labelKey], value: item[valueKey] }));

const withTrimmedForm = (onFinish) => (values) => {
  const trimmedValues = Object.fromEntries(
    Object.entries(values).map(([key, value]) => [key, value && typeof value === 'string' ? value.trim() : value])
  );
  onFinish(trimmedValues);
};

const getInvoiceStatus = ({ oper_facturado, efact_descripcion, errtim_descripcion }) => {
  const isSuccess = errtim_descripcion?.toLowerCase().includes('comando exitoso');

  // --------------------------
  // CASE 1: oper_facturado = 1
  // --------------------------
  if (oper_facturado === 1) {
    return {
      statusLabel: 'Facturado',
      statusColor: 'green'
    };
  }

  // --------------------------
  // CASE 2: oper_facturado = 0
  // --------------------------
  const efact = Number(efact_descripcion);

  // --- efact_descripcion > 10 → Always Error ---
  if (efact > 10) {
    return {
      statusLabel: 'Error',
      statusColor: 'red'
    };
  }

  // --- efact_descripcion between 0–10 ---
  if (efact >= 0 && efact <= 10) {
    if (isSuccess) {
      return {
        statusLabel: 'Facturado',
        statusColor: 'green'
      };
    } else {
      return {
        statusLabel: 'Pendiente',
        statusColor: 'purple'
      };
    }
  }

  // Fallback (unreachable but safe)
  return {
    statusLabel: 'Pendiente',
    statusColor: 'orange'
  };
};

export {
  allowOnlyNumbers,
  buildIncomeUrl,
  capitalizeFirstLetter,
  currencyFormatter,
  fillCeroIfEmpty,
  hasData,
  hasValue,
  isCreate,
  isEdit,
  multiply,
  noFormatCurrency,
  removeColumns,
  replaceEmptyRecords,
  sortByActive,
  sortColumns,
  sum,
  valueToUpperCase,
  getSelectOptions,
  withTrimmedForm,
  getInvoiceStatus
};
